import * as Sentry from "@sentry/react";
import type { AxiosError } from "axios";

import { isClientOutdatedAtom, store } from "~/atoms";

/**
 * This bundle is static on Cloudflare and the API deploys separately, so a tab
 * left open across a deploy keeps calling the shape it was built against. The
 * server answers such a request with 426 and the frontend reloads into the
 * current bundle.
 */

/**
 * Raised by hand, in the PR that adopts a new server shape, to the value the
 * server's `MIN_CLIENT_VERSION` will be raised to when the old shape goes.
 */
export const CLIENT_VERSION = 5;

export const CLIENT_VERSION_HEADER = "X-Client-Version";

export const UPGRADE_REQUIRED_STATUS = 426;

const REFUSED_VERSION_KEY = "meq-refused-client-version";

/**
 * Keyed on the version so it clears itself: a note left by an earlier bundle
 * never suppresses the reload a newer one is owed, and a repeat against the
 * SAME version can only mean the bundle being served is itself below the floor.
 */
const readRefusedVersion = (): number | null => {
  try {
    const stored = sessionStorage.getItem(REFUSED_VERSION_KEY);
    return stored === null ? null : Number(stored);
  } catch {
    return null;
  }
};

const writeRefusedVersion = (version: number): void => {
  try {
    sessionStorage.setItem(REFUSED_VERSION_KEY, String(version));
  } catch {
    // Storage blocked (private mode, cookies off). Losing the guard costs one
    // extra reload, which beats never reloading at all.
  }
};

const readMinClientVersion = (error: AxiosError): number | undefined => {
  const { minClientVersion } = (error.response?.data ?? {}) as {
    minClientVersion?: unknown;
  };
  return typeof minClientVersion === "number" ? minClientVersion : undefined;
};

/**
 * Returns a promise that never settles, so React Query leaves the query pending
 * instead of firing an error snackbar over a page that is either reloading or
 * already covered by the outdated-client notice.
 */
const stopHere = (): Promise<never> => new Promise<never>(() => {});

/**
 * A stale tab's queries are refused together, and `location.reload()` does not
 * unload the page before the next 426 lands. Without this, the second refusal
 * reads the note the first just wrote and reports a failed reload that is
 * still in flight.
 */
let isReloading = false;

/** Test-only: a new page load starts with no reload in flight. */
export const __resetClientVersionForTests = (): void => {
  isReloading = false;
};

/**
 * Handles the server's "not with this client" refusal, or returns null so the
 * caller falls through to its normal error mapping.
 */
export const handleUpgradeRequired = (
  error: AxiosError,
): Promise<never> | null => {
  if (error.response?.status !== UPGRADE_REQUIRED_STATUS) return null;
  if (isReloading) return stopHere();

  if (readRefusedVersion() === CLIENT_VERSION) {
    // Reloading already happened and produced this same bundle, so the deploy
    // being served is itself below the floor — a deploy ordering mistake.
    Sentry.captureMessage("Client refused after reloading into same version", {
      level: "error",
      extra: {
        clientVersion: CLIENT_VERSION,
        minClientVersion: readMinClientVersion(error),
        url: error.config?.url,
      },
    });
    store.set(isClientOutdatedAtom, true);
    return stopHere();
  }

  writeRefusedVersion(CLIENT_VERSION);
  isReloading = true;
  window.location.reload();
  return stopHere();
};
